import { useState } from 'react';
import { X } from 'lucide-react';
import type { ArrayAssemblyResult, EnrichedSensorSpec, NewArrayAssembly } from '../../services/sensorBoxArrays';

type CompletionModalProps = {
  boxId: string;
  result?: ArrayAssemblyResult | null;
  pending?: NewArrayAssembly | null;
  extraSensors?: EnrichedSensorSpec[];
  onClose: () => void;
  onCreateAnother?: () => void;
};

export default function CompletionModal({
  boxId,
  result,
  pending,
  extraSensors = [],
  onClose,
  onCreateAnother,
}: CompletionModalProps) {
  const [copied, setCopied] = useState(false);
  const success = Boolean(result);

  const copyReadKey = async () => {
    if (!result?.readKey) return;
    try {
      await navigator.clipboard.writeText(result.readKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-xl text-[#004F56] pr-2">
            {success ? 'Sensor array created' : 'Waiting on the array API'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 shrink-0"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <>
            <dl className="grid sm:grid-cols-2 gap-3 text-sm text-gray-700 mb-4">
              <div>
                <dt className="text-gray-500">Sensor array ID</dt>
                <dd className="font-medium text-lg text-[#004F56]">{result.sensorArrayID}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Box ID</dt>
                <dd className="font-medium">{boxId}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">readKey</dt>
                <dd className="mt-1 flex flex-col sm:flex-row sm:items-center gap-2">
                  <code className="flex-1 break-all rounded-md bg-gray-50 px-3 py-2 text-xs">
                    {result.readKey || 'Not returned by the API'}
                  </code>
                  {result.readKey && (
                    <button
                      type="button"
                      onClick={() => void copyReadKey()}
                      className="shrink-0 self-start text-sm font-medium text-teal-700 hover:underline"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </dd>
                {result.readKey && (
                  <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Save this key now. Later lookups only show a hash, so you will not be able to
                    recover the original value.
                  </p>
                )}
              </div>
            </dl>

            <p className="text-sm font-medium text-gray-800 mb-2">Sensors</p>

            <ul className="sm:hidden space-y-2 mb-1">
              {result.sensors.map((sensor) => (
                <li key={sensor.id} className="rounded-xl border border-gray-200 px-3 py-3 text-sm">
                  <p className="font-medium text-gray-900">
                    {sensor.readValLabel}{' '}
                    <span className="text-gray-500 font-normal">· ID {sensor.id}</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {sensor.type}
                    {sensor.unitAbbreviation ? ` · ${sensor.unitAbbreviation}` : ''}
                    {sensor.unit ? ` · ${sensor.unit}` : ''}
                  </p>
                </li>
              ))}
            </ul>

            <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">id</th>
                    <th className="px-3 py-2 font-medium">type</th>
                    <th className="px-3 py-2 font-medium">readValLabel</th>
                    <th className="px-3 py-2 font-medium">unit</th>
                    <th className="px-3 py-2 font-medium">unitAbbreviation</th>
                  </tr>
                </thead>
                <tbody>
                  {result.sensors.map((sensor) => (
                    <tr key={sensor.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-900">{sensor.id}</td>
                      <td className="px-3 py-2">{sensor.type}</td>
                      <td className="px-3 py-2">{sensor.readValLabel}</td>
                      <td className="px-3 py-2">{sensor.unit}</td>
                      <td className="px-3 py-2">{sensor.unitAbbreviation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Box ID {boxId} is ready. Creating the array is waiting on the Seawall API. Below is
              what we would send once those routes are available.
            </p>
            {extraSensors.length > 0 && (
              <ul className="mb-4 space-y-1 text-sm text-gray-700">
                {extraSensors.map((sensor) => (
                  <li key={sensor.id}>
                    {sensor.readValLabel} ({sensor.type}
                    {sensor.unitAbbreviation ? `, ${sensor.unitAbbreviation}` : ''}) · spec ID {sensor.id}
                  </li>
                ))}
              </ul>
            )}
            {pending && (
              <pre className="overflow-x-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">
                {JSON.stringify(pending, null, 2)}
              </pre>
            )}
          </>
        )}

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          {success && onCreateAnother && (
            <button
              type="button"
              onClick={onCreateAnother}
              className="w-full h-11 rounded-lg border border-[#005E6E] text-[#005E6E] font-semibold hover:bg-teal-50"
            >
              Create another
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
